import { useRef, useState } from 'react';
import { HbsModal } from './hbs/Modal';
import { HbsButton } from './hbs/Button';
import { HbsInput } from './hbs/Input';
import { EyeToggle } from './hbs/EyeToggle';
import { useSafe } from '../context/SafeContext';
import {
  decryptSafeExport, toImportedItems, itemTypeCounts, passkeyCount,
  WrongPassphraseError, MalformedBackupError, SAFE_EXPORT_EXTENSION,
  type VaultItem,
} from '@heybesafe/shared';

interface RestoreSafeModalProps {
  open: boolean;
  onClose: () => void;
}

type State =
  | { kind: 'pick'; error: string | null }
  | { kind: 'decrypting' }
  | { kind: 'preview'; items: VaultItem[]; exportedAt: string }
  | { kind: 'restoring' }
  | { kind: 'done'; count: number };

/** Friendly plural label per item type for the restore summary. */
const TYPE_LABEL: Record<string, [string, string]> = {
  login: ['login', 'logins'],
  note: ['note', 'notes'],
  card: ['card', 'cards'],
  identity: ['identity', 'identities'],
  passkey: ['passkey', 'passkeys'],
  contact: ['contact', 'contacts'],
  document: ['document', 'documents'],
  schedule: ['schedule', 'schedules'],
};

function summarize(items: VaultItem[]): string {
  const counts = itemTypeCounts(items);
  const parts = Object.entries(counts).map(([type, n]) => {
    const [one, many] = TYPE_LABEL[type] ?? [type, `${type}s`];
    return `${n} ${n === 1 ? one : many}`;
  });
  const pk = passkeyCount(items);
  if (pk > 0) parts.push(`${pk} with ${pk === 1 ? 'a passkey' : 'passkeys'}`);
  return parts.join(', ');
}

export function RestoreSafeModal({ open, onClose }: RestoreSafeModalProps): JSX.Element | null {
  const { addItem } = useSafe();
  const [file, setFile] = useState<File | null>(null);
  const [passphrase, setPassphrase] = useState('');
  const [reveal, setReveal] = useState(false);
  const [state, setState] = useState<State>({ kind: 'pick', error: null });
  const fileInputRef = useRef<HTMLInputElement>(null);

  function reset(): void {
    setFile(null);
    setPassphrase('');
    setReveal(false);
    setState({ kind: 'pick', error: null });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleClose(): void {
    if (state.kind === 'decrypting' || state.kind === 'restoring') return;
    reset();
    onClose();
  }

  async function handleDecrypt(): Promise<void> {
    if (!file || passphrase.length === 0) return;
    setState({ kind: 'decrypting' });
    try {
      const text = await file.text();
      const { items, exportedAt } = await decryptSafeExport(text, passphrase);
      if (items.length === 0) {
        setState({ kind: 'pick', error: 'This backup has no items to restore.' });
        return;
      }
      setState({ kind: 'preview', items, exportedAt });
    } catch (e) {
      const error =
        e instanceof WrongPassphraseError ? 'Wrong password for this backup, or the file has been altered.'
        : e instanceof MalformedBackupError ? e.message
        : 'Could not read that backup file.';
      setPassphrase('');
      setState({ kind: 'pick', error });
    }
  }

  async function handleConfirmRestore(items: VaultItem[]): Promise<void> {
    setState({ kind: 'restoring' });
    let count = 0;
    // Restore adds fresh items (new ids/timestamps) — it never overwrites, so a
    // restore into a non-empty safe merges rather than replaces.
    for (const item of toImportedItems(items)) {
      try { await addItem(item); count++; } catch { /* skip a bad item, keep going */ }
    }
    setState({ kind: 'done', count });
  }

  if (!open) return null;

  const busy = state.kind === 'decrypting' || state.kind === 'restoring';

  return (
    <HbsModal
      open={open}
      onClose={handleClose}
      title="Restore from a backup"
      size="md"
      dismissOnBackdrop={!busy}
      dismissOnEsc={!busy}
    >
      {state.kind === 'done' ? (
        <>
          <p>Restored {state.count} {state.count === 1 ? 'item' : 'items'} into your safe.</p>
          <div className="mt-6 flex justify-end">
            <HbsButton id="restore-close-button" onClick={handleClose}>Close</HbsButton>
          </div>
        </>
      ) : state.kind === 'preview' ? (
        <div className="space-y-3">
          <p>
            This backup was made on{' '}
            <span className="font-medium">{new Date(state.exportedAt).toLocaleDateString()}</span> and
            contains:
          </p>
          <p className="text-hbs-text-mute">{summarize(state.items)}.</p>
          <p className="text-hbs-text-mute text-[13px]">
            Restoring adds these to your current safe — it doesn’t replace anything. Passkeys are
            restored as working credentials.
          </p>
          <div className="mt-6 flex justify-end gap-2">
            <HbsButton type="button" variant="ghost" id="restore-preview-cancel-button" onClick={handleClose}>
              Cancel
            </HbsButton>
            <HbsButton
              id="restore-confirm-button"
              onClick={() => { void handleConfirmRestore(state.items); }}
            >
              Restore {state.items.length} {state.items.length === 1 ? 'item' : 'items'}
            </HbsButton>
          </div>
        </div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); void handleDecrypt(); }} className="space-y-3">
          <p className="text-hbs-text-mute text-[13px]">
            Choose a Hey Be Safe backup file (<code>.{SAFE_EXPORT_EXTENSION}</code>) and enter the
            safe password you used when you created it.
          </p>
          <input
            ref={fileInputRef}
            id="restore-file-input"
            type="file"
            accept={`.${SAFE_EXPORT_EXTENSION},application/json`}
            disabled={busy}
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              if (state.kind === 'pick' && state.error) setState({ kind: 'pick', error: null });
            }}
            className="block w-full text-sm"
          />
          <HbsInput
            type={reveal ? 'text' : 'password'}
            label="Backup password"
            value={passphrase}
            onChange={setPassphrase}
            mono={reveal}
            trailingActions={<EyeToggle revealed={reveal} onChange={setReveal} />}
          />
          {state.kind === 'pick' && state.error && (
            <p role="alert" className="text-hbs-danger text-[13px]">{state.error}</p>
          )}
          <div className="mt-6 flex justify-end gap-2">
            <HbsButton type="button" variant="ghost" id="restore-cancel-button" onClick={handleClose} disabled={busy}>
              Cancel
            </HbsButton>
            <HbsButton
              id="restore-decrypt-button"
              type="submit"
              disabled={!file || passphrase.length === 0 || busy}
              loading={busy}
            >
              Continue
            </HbsButton>
          </div>
        </form>
      )}
    </HbsModal>
  );
}
